import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Check } from "lucide-react";
import { toast } from "sonner";
import { submitInquiry } from "@/lib/api";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const PROJECT_TYPES = [
  "Full-Home Interior Design",
  "New Build / Architecture",
  "Renovation",
  "Single Room",
  "The Hue Edit / Sourcing",
  "Something else",
];

export default function ContactDialog({ open, mode = "start", onClose }) {
  const isContact = mode === "contact";
  const [form, setForm] = useState({ name: "", email: "", phone: "", project_type: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const reset = () => {
    setForm({ name: "", email: "", phone: "", project_type: "", message: "" });
    setDone(false);
    setSubmitting(false);
  };
  const close = () => {
    onClose();
    setTimeout(reset, 400);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please share your name, email, and a few words.");
      return;
    }
    setSubmitting(true);
    try {
      await submitInquiry({ ...form, source: isContact ? "contact" : "start-a-project" });
      setDone(true);
      toast.success("Thank you — your note is on its way to the studio.");
    } catch (err) {
      toast.error("Something went wrong. Please try again in a moment.");
      setSubmitting(false);
    }
  };

  const inputClass =
    "w-full bg-transparent border-b border-[#d6ccbb] focus:border-[#23221f] outline-none py-3 text-[#23221f] placeholder:text-[#a89f8d] transition-colors duration-300 font-light";
  const labelClass = "text-[10px] uppercase tracking-[0.22em] text-[#9c7a4e] mb-1 block";

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="contact-dialog"
          className="fixed inset-0 z-[90] flex items-stretch md:items-center justify-end md:justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4 }}
        >
          <div className="absolute inset-0 bg-[#141310]/50 backdrop-blur-sm" onClick={close} data-testid="contact-backdrop" />

          <motion.div
            className="relative w-full md:max-w-lg bg-[#FBFAF6] h-full md:h-auto md:max-h-[92vh] overflow-y-auto"
            initial={{ x: "100%", opacity: 0.6 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: "100%", opacity: 0.6 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            data-lenis-prevent
          >
            <button
              data-testid="contact-close"
              onClick={close}
              className="absolute top-6 right-6 text-[#23221f] hover:text-[#9c7a4e] transition-colors z-10"
              aria-label="Close"
            >
              <X size={22} />
            </button>

            <div className="px-7 md:px-12 py-12 md:py-14">
              {!done ? (
                <>
                  <p className="text-[11px] uppercase tracking-[0.3em] text-[#9c7a4e] mb-5">
                    {isContact ? "Contact" : "Start a Project"}
                  </p>
                  <h2 className="font-display font-light text-[#23221f] text-4xl md:text-5xl leading-[1.03] mb-3">
                    {isContact ? "Let's talk." : "Tell us about your home."}
                  </h2>
                  <p className="text-[#6b6459] font-light mb-8 leading-relaxed">
                    Share a little about your space and the life you want to live in it. We&rsquo;ll be in touch soon.
                  </p>

                  <form onSubmit={handleSubmit} className="space-y-5 md:space-y-7">
                    <div>
                      <label className={labelClass} htmlFor="c-name">Name</label>
                      <input id="c-name" data-testid="contact-name" className={inputClass} value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="Your full name" />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      <div>
                        <label className={labelClass} htmlFor="c-email">Email</label>
                        <input id="c-email" type="email" data-testid="contact-email" className={inputClass} value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="you@email.com" />
                      </div>
                      <div>
                        <label className={labelClass} htmlFor="c-phone">Phone <span className="lowercase tracking-normal text-[#a89f8d]">(optional)</span></label>
                        <input id="c-phone" data-testid="contact-phone" className={inputClass} value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="&mdash;" />
                      </div>
                    </div>
                    <div>
                      <label className={labelClass}>Project</label>
                      <Select value={form.project_type} onValueChange={(v) => update("project_type", v)}>
                        <SelectTrigger data-testid="contact-project-type" className="w-full bg-transparent border-0 border-b border-[#d6ccbb] rounded-none px-0 py-3 h-auto text-[#23221f] focus:ring-0 focus:border-[#23221f]">
                          <SelectValue placeholder="Select a focus" />
                        </SelectTrigger>
                        <SelectContent className="z-[110] bg-[#FBFAF6] border-[#e3ddd0]">
                          {PROJECT_TYPES.map((p) => (
                            <SelectItem key={p} value={p} data-testid={`project-opt-${p}`}>{p}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className={labelClass} htmlFor="c-message">Your vision</label>
                      <textarea id="c-message" data-testid="contact-message" rows={4} className={`${inputClass} resize-none`} value={form.message} onChange={(e) => update("message", e.target.value)} placeholder="A few words about your home and how you want it to feel..." />
                    </div>

                    <button type="submit" data-testid="contact-submit" disabled={submitting} className="w-full text-[12px] tracking-[0.2em] uppercase px-8 py-4 bg-[#23221f] text-[#F5F3EE] hover:bg-[#9c7a4e] transition-colors duration-500 disabled:opacity-60">
                      {submitting ? "Sending…" : "Send Inquiry"}
                    </button>
                  </form>
                </>
              ) : (
                <div className="py-16 text-center" data-testid="contact-success">
                  <div className="mx-auto w-14 h-14 rounded-full border border-[#9c7a4e] flex items-center justify-center text-[#9c7a4e] mb-8">
                    <Check size={22} />
                  </div>
                  <h2 className="font-display font-light text-[#23221f] text-4xl mb-4">Thank you.</h2>
                  <p className="text-[#6b6459] font-light max-w-sm mx-auto leading-relaxed mb-10">
                    Your note is on its way to the studio. We read every message personally and will be in touch soon.
                  </p>
                  <button data-testid="contact-done" onClick={close} className="text-[12px] tracking-[0.2em] uppercase border-b border-[#23221f] pb-1 hover:text-[#9c7a4e] hover:border-[#9c7a4e] transition-colors">
                    Close
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
